import { useMemo, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  History,
  SendHorizontal,
  ChevronRight,
  ArrowLeft,
  User,
  Plus,
  FileText,
  Pencil,
  Trash2,
  Loader2,
  Globe,
  ExternalLink,
  ChevronDown,
  ChevronsUp,
  ChevronsDown,
} from "lucide-react";
import {
  useGetTeacherChat,
  useGetTeacherChats,
  getGetTeacherChatQueryKey,
  useSendMessageToTeacherChat,
  deleteTeacherChat,
  useUpdateTeacherChat,
} from "@packages/api/madlenAPI";
import {
  Link,
  useParams,
  useSearchParams,
  useNavigate,
} from "react-router-dom";
import {
  GetChatResponseDto,
  ListTeacherChatResponseDto,
  TeacherChatMessageResponseDto,
  TeacherChatSendMessageRequestDtoContentType,
} from "@packages/api/madlenAPI.schemas";
import { useQueryClient } from "@tanstack/react-query";
import { assistantColorSchemes } from "@packages/utils";
import remarkGfm from "remark-gfm";
import { useAppDispatch, useAppSelector } from "@packages/store/hooks";
import SingleAssistantEssayGrader from "../../components/SingleAssistantEssayGrader";
import { useToast } from "@packages/contexts/ToastContext";
import { trackEvent } from "@packages/hooks/useAnalytics";
import Modal from "@core/modal";
import { Text, Button, SimpleLoading } from "@core/index";
import { FileUploader } from "@core/file-uploader";
import { format } from "date-fns";
import { tr, enUS } from "date-fns/locale";
import DeleteConfirmPopUp from "@features/tools/components/tool_components/DeleteConfirmPopUp";
import { ModelButtonSelector, availableModels } from "./model-button-selector";
import { setAssistantModel } from "@packages/store/userSlice";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import ReactMarkdown from "react-markdown";
import "katex/dist/katex.min.css";

const FilePreview = ({
  fileName,
  fileUrl,
}: {
  fileName: string;
  fileUrl: string;
}) => {
  return (
    <Link
      to={fileUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="rounded-full p-4 py-2 border border-dark-400 dark:border-dark-400"
    >
      <div className="flex items-center">
        <div className="">
          <FileText className="w-4 h-4 text-gray-400 dark:text-gray-400" />
        </div>
        <h3 className="font-medium text-gray-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors truncate pl-2">
          {fileName}
        </h3>
      </div>
    </Link>
  );
};

const SourceChip = ({
  domain,
  url,
  sourceId,
  onSourceHover,
}: {
  domain: string;
  url: string;
  sourceId: string;
  onSourceHover: (sourceId: string | null) => void;
}) => {
  // Format domain name (remove .com, .org, etc. and capitalize)
  const formatDomain = (domain: string): string => {
    // Extract the domain name without TLD
    const domainName = domain.split(".")[0];
    // Capitalize first letter
    return domainName.charAt(0).toUpperCase() + domainName.slice(1);
  };

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/30 dark:hover:bg-blue-800/50 text-blue-700 dark:text-blue-300 text-xs px-3 py-1 rounded-full mr-2 mb-2 transition-colors border border-blue-200 dark:border-blue-700 group"
      onMouseEnter={() => onSourceHover(sourceId)}
      onMouseLeave={() => onSourceHover(null)}
    >
      <ExternalLink className="w-3 h-3 mr-1 text-blue-500 dark:text-blue-400 group-hover:text-blue-600 dark:group-hover:text-blue-300" />
      {formatDomain(domain)}
    </a>
  );
};

const ChatBubble = ({
  message,
  avatar,
  selectedModel,
  isFirstMessage,
}: {
  message: TeacherChatMessageResponseDto;
  avatar: string | null;
  selectedModel: string;
  isFirstMessage: boolean;
}) => {
  const isUser = message.role === "USER";
  const { t, i18n } = useTranslation();
  const [activeSourceId, setActiveSourceId] = useState<string | null>(null);
  const messageContainerRef = useRef<HTMLDivElement>(null);

  // Get the selected model's color scheme
  const modelColorScheme = useMemo(() => {
    const model =
      availableModels.find((m) => m.id === selectedModel) || availableModels[2]; // Default to Claude
    return {
      primaryColor: model.color.replace("text-", ""),
      bgColor: model.bgColor.replace("bg-", ""),
      activeBgColor: model.activeBgColor.replace("bg-", ""),
    };
  }, [selectedModel]);

  // Process message to remove citation markers
  const processedMessage = useMemo(() => {
    if (!message.message) return "";

    // If no grounding metadata, return message as is
    if (!message.groundingMetadata)
      return message.message.replace(/\\n/g, "\n");

    // Replace citation markers like [1], [2,3], etc. with empty string
    return message.message
      .replace(/\[\d+(?:,\s*\d+)*\]/g, "")
      .replace(/\*\*(.*?)\*\*/g, "$1")
      .replace(/\*(.*?)\*/g, "$1")
      .replace(/`(.*?)`/g, "$1")
      .replace(/\[(.*?)\]\(.*?\)/g, "$1")
      .replace(/\\n/g, "\n");
  }, [message.message, message.groundingMetadata]);

  // Extract source data for showing chips
  const sourceChips = useMemo(() => {
    if (!message.groundingMetadata) return [];

    return Object.entries(message.groundingMetadata)
      .map(([id, data]) => ({
        id,
        domain: data.domain || "",
        url: data.url || "",
      }))
      .filter((source) => source.domain && source.url);
  }, [message.groundingMetadata]);

  // Effect to handle highlighting when activeSourceId changes
  useEffect(() => {
    if (
      !messageContainerRef.current ||
      !message.groundingSegments ||
      !message.groundingMetadata ||
      isUser
    ) {
      return;
    }

    // Clear previous highlights
    const previousHighlights =
      messageContainerRef.current.querySelectorAll(".highlight-segment");
    previousHighlights.forEach((el) => {
      const parent = el.parentNode;
      if (parent) {
        // Replace the highlight span with its text content
        parent.replaceChild(document.createTextNode(el.textContent || ""), el);
        // Normalize to merge adjacent text nodes
        parent.normalize();
      }
    });

    // If no active source, we're done
    if (!activeSourceId) return;

    // Find segments that reference the active source
    const relevantSegments = message.groundingSegments.filter((segment) => {
      // First check if this segment directly references the active source
      const directReference = segment.groundingChunkIndices?.some(
        (index: number) =>
          index.toString() === activeSourceId ||
          (message.groundingMetadata &&
            message.groundingMetadata[index.toString()]?.domain ===
              message.groundingMetadata[activeSourceId]?.domain),
      );

      if (directReference) return true;

      // Special case for the first segment which often contains the main statement
      // If the active source is referenced in any segment, we may want to highlight
      // the main statement as well depending on the content
      if (
        message.groundingSegments &&
        message.groundingSegments[0] === segment
      ) {
        // Check if this appears to be a main statement (contains the key fact)
        const segmentText = segment.segment?.text || "";
        const lowerText = segmentText.toLowerCase();

        // Add specific checks based on common patterns in AI responses
        // For example, check if this segment contains phrases like "is", "currently is", etc.
        if (
          lowerText.includes("currently") ||
          lowerText.includes("is ") ||
          lowerText.includes("şu anki") ||
          lowerText.includes("başkan") ||
          lowerText.includes("president")
        ) {
          return true;
        }
      }

      return false;
    });

    if (relevantSegments.length === 0 || !messageContainerRef.current) return;

    // Function to find and highlight all occurrences of text in the DOM
    const findAndHighlightTextInDOM = (rootNode: Node, searchText: string) => {
      const treeWalker = document.createTreeWalker(
        rootNode,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: (node) => {
            // Skip text nodes within elements we don't want to process
            const parent = node.parentElement;
            if (
              parent?.classList.contains("highlight-segment") ||
              parent?.closest(".source-chip-container") ||
              parent?.classList.contains("copy-button")
            ) {
              return NodeFilter.FILTER_REJECT;
            }
            return NodeFilter.FILTER_ACCEPT;
          },
        },
      );

      // Collect all text nodes first to avoid modifying during traversal
      const textNodes: Text[] = [];
      let currentNode: Node | null;
      while ((currentNode = treeWalker.nextNode())) {
        textNodes.push(currentNode as Text);
      }

      // Process each text node
      let foundMatch = false;
      for (const textNode of textNodes) {
        const nodeText = textNode.textContent || "";

        // Try exact match first
        const exactIndex = nodeText.indexOf(searchText);
        if (exactIndex >= 0) {
          highlightMatchInNode(textNode, exactIndex, searchText.length);
          foundMatch = true;
          continue;
        }

        // If exact match fails and text is long enough, try fuzzy matching
        // by looking for substantial portions of the text
        if (searchText.length > 15) {
          // Try to match first 75% of the search text
          const partialSearchText = searchText.substring(
            0,
            Math.floor(searchText.length * 0.75),
          );
          if (
            partialSearchText.length > 10 &&
            nodeText.includes(partialSearchText)
          ) {
            const partialIndex = nodeText.indexOf(partialSearchText);
            // Find how much of the search text is actually in the node
            let matchLength = partialSearchText.length;
            for (let i = partialSearchText.length; i < searchText.length; i++) {
              if (nodeText[partialIndex + i] === searchText[i]) {
                matchLength++;
              } else {
                break;
              }
            }
            highlightMatchInNode(textNode, partialIndex, matchLength);
            foundMatch = true;
          }
        }
      }

      return foundMatch;
    };

    // Helper to highlight a specific portion of a text node
    const highlightMatchInNode = (
      textNode: Text,
      startIndex: number,
      length: number,
    ) => {
      // Create three parts: before, highlight, after
      const before = document.createTextNode(
        textNode.textContent?.substring(0, startIndex) || "",
      );
      const highlight = document.createElement("span");
      highlight.className =
        "highlight-segment bg-yellow-100 dark:bg-yellow-900/40 px-0.5 rounded transition-colors duration-200";
      highlight.textContent =
        textNode.textContent?.substring(startIndex, startIndex + length) || "";
      const after = document.createTextNode(
        textNode.textContent?.substring(startIndex + length) || "",
      );

      // Replace the original node with our three parts
      const parent = textNode.parentNode;
      if (parent) {
        parent.insertBefore(before, textNode);
        parent.insertBefore(highlight, textNode);
        parent.insertBefore(after, textNode);
        parent.removeChild(textNode);
      }
    };

    // Process the segments in a more intelligent order to prioritize key facts
    const processedSegments = [...relevantSegments].sort((a, b) => {
      // Always process the first segment (main statement) first if it's included
      if (message.groundingSegments && message.groundingSegments[0] === a)
        return -1;
      if (message.groundingSegments && message.groundingSegments[0] === b)
        return 1;

      // Process shorter segments later (they're more specific and less likely to cause issues)
      const aLen = a.segment?.text?.length || 0;
      const bLen = b.segment?.text?.length || 0;
      return bLen - aLen;
    });

    // Process each segment for highlighting
    for (const segmentData of processedSegments) {
      if (!segmentData.segment?.text) continue;

      // Extract any strong/bold content first - it might be the key entity
      const boldMatches = segmentData.segment.text.match(/\*\*(.*?)\*\*/g);
      const boldTexts = boldMatches?.map((m) => m.replace(/\*\*/g, "")) || [];

      // Get the text content in different formats to try
      const formats = [
        // Original with Markdown formatting removed
        segmentData.segment.text
          .replace(/\[\d+(?:,\s*\d+)*\]/g, "") // Remove citation markers
          .replace(/\*\*(.*?)\*\*/g, "$1") // Remove Markdown bold syntax
          .replace(/\*(.*?)\*/g, "$1") // Remove Markdown italic syntax
          .replace(/`(.*?)`/g, "$1") // Remove Markdown code syntax
          .replace(/\[(.*?)\]\(.*?\)/g, "$1") // Remove Markdown links
          .trim(),

        // If there are bold segments, try those separately as they're often the focus
        ...boldTexts,

        // Simplified version without punctuation (as a fallback)
        segmentData.segment.text
          .replace(/\[\d+(?:,\s*\d+)*\]/g, "")
          .replace(/\*\*(.*?)\*\*/g, "$1")
          .replace(/[.,'"!?;:()\[\]{}]/g, "")
          .replace(/\s+/g, " ")
          .trim(),
      ];

      // Try each format until one succeeds
      let found = false;
      for (const textToSearch of formats) {
        if (textToSearch.length < 5) continue; // Skip very short segments

        if (messageContainerRef.current) {
          found = findAndHighlightTextInDOM(
            messageContainerRef.current,
            textToSearch,
          );
          if (found) break;
        }
      }

      // If simplified formats didn't work, original logic as fallback
      if (!found && segmentData.segment.text.length > 5) {
        // The original code's approach for reference
        const segmentText = segmentData.segment.text
          .replace(/\[\d+(?:,\s*\d+)*\]/g, "")
          .replace(/\*\*(.*?)\*\*/g, "$1")
          .replace(/\*(.*?)\*/g, "$1")
          .replace(/`(.*?)`/g, "$1")
          .replace(/\[(.*?)\]\(.*?\)/g, "$1")
          .trim();

        if (messageContainerRef.current) {
          findAndHighlightTextInDOM(messageContainerRef.current, segmentText);
        }
      }
    }
  }, [
    activeSourceId,
    message.groundingSegments,
    message.groundingMetadata,
    isUser,
  ]);

  if (!message || !message.message || message.message?.trim() === "")
    return null;

  const handleCopy = () => {
    navigator.clipboard.writeText(message.message || "");
  };

  const handleSourceHover = (sourceId: string | null) => {
    setActiveSourceId(sourceId);
  };

  return (
    <div
      data-testid={isUser ? "user-message" : "bot-message"}
      className={`flex ${isUser ? "justify-end" : "justify-start"} mb-2`}
    >
      <div
        className={`flex ${isUser ? "flex-row-reverse" : "flex-row"} items-end max-w-[80%]`}
      >
        <div
          className={`flex flex-col ${isUser ? "items-end" : "items-start"}`}
        >
          <div
            className={`px-4 py-2 rounded-2xl break-words relative
            ${
              isUser
                ? "bg-dark-600 text-white rounded-br-none"
                : "text-gray-900 dark:text-gray-100 bg-transparent shadow-none border-none"
            }`}
            style={{
              boxShadow: "none",
              border: "none",
              background: isUser ? undefined : "none",
            }}
          >
            <div ref={messageContainerRef}>
              <ReactMarkdown
                className={`prose dark:prose-invert max-w-none 
                ${isUser ? "text-white prose-headings:text-white prose-strong:text-white prose-em:text-white" : ""} 
                prose-table:border prose-table:border-collapse prose-td:border prose-td:p-2 prose-th:border prose-th:p-2 prose-th:bg-gray-100 dark:prose-th:bg-gray-700
                prose-pre:whitespace-pre-wrap prose-pre:break-words prose-pre:overflow-auto prose-pre:max-w-full`}
                remarkPlugins={[remarkGfm, remarkMath]}
                rehypePlugins={[rehypeKatex]}
              >
                {processedMessage}
              </ReactMarkdown>
            </div>
            {/* Source citations as chips */}
            {!isUser && sourceChips.length > 0 && (
              <div className="mt-2 pt-2 source-chip-container flex flex-wrap gap-2">
                {sourceChips.map((source, index) => (
                  <SourceChip
                    key={index}
                    domain={source.domain}
                    url={source.url}
                    sourceId={source.id}
                    onSourceHover={handleSourceHover}
                  />
                ))}
              </div>
            )}
            {/* Copy button, right-aligned and minimal */}
            <div
              className={`flex ${isUser ? "justify-end" : "justify-start"} mt-2`}
            >
              <button
                onClick={handleCopy}
                className={`px-3 py-1 rounded-md flex items-center gap-2 border text-xs transition-colors
                  ${
                    isUser
                      ? "bg-dark-900 border-dark-500 text-white hover:bg-dark-500"
                      : "bg-dark-400 border-dark-500 text-white hover:bg-dark-500"
                  }
                `}
                title={t("common.copy")}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
                {t("common.copy")}
              </button>
            </div>
          </div>
          <div className={`text-xs text-gray-500 dark:text-gray-400 mt-1`}>
            {message.createdAt
              ? new Date(message.createdAt).toLocaleTimeString(i18n.language, {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: i18n.language === "tr" ? false : true,
                })
              : ""}
          </div>
        </div>
      </div>
    </div>
  );
};

const SingleAssistantWrapper = ({
  isSidebarCollapsed,
}: {
  isSidebarCollapsed: boolean;
}) => {
  const [searchParams] = useSearchParams();
  const isEssayGrader = searchParams.get("isEssayGrader") === "true";

  if (isEssayGrader) {
    return (
      <SingleAssistantEssayGrader isSidebarCollapsed={isSidebarCollapsed} />
    );
  }

  return <SingleAssistant isSidebarCollapsed={isSidebarCollapsed} />;
};

const SingleAssistant = ({
  isSidebarCollapsed,
}: {
  isSidebarCollapsed: boolean;
}) => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [message, setMessage] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [isTopBarCollapsed, setIsTopBarCollapsed] = useState(false);
  const queryClient = useQueryClient();
  const avatar = useAppSelector((state) => state.user.avatar);
  const [isUploading, setIsUploading] = useState(false);
  // const [uploadProgress, setUploadProgress] = useState(0);
  const [newFile, setNewFile] = useState<{
    name: string;
    s3Url: string;
  } | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<
    {
      name: string;
      s3Url: string;
    }[]
  >([]);
  const { addToast } = useToast();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [editChatId, setEditChatId] = useState<number | null>(null);
  const [editChatTitle, setEditChatTitle] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState<number | undefined>(
    undefined,
  );
  const [isWebSearchEnabled, setIsWebSearchEnabled] = useState(false);
  const selectedModelRaw = useAppSelector(
    (state) =>
      state.user.teacher?.preferences?.preferredLanguageModel ||
      "claude-3.7-sonnet",
  );
  const [isModelChangeLoading, setIsModelChangeLoading] = useState(false);
  const [loadingModelId, setLoadingModelId] = useState<string | null>(null);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { mutate: updateTeacherChat } = useUpdateTeacherChat({
    mutation: {
      onMutate: (variables) => {
        const previousSingleChatData = queryClient.getQueryData(
          getGetTeacherChatQueryKey(Number(id)),
        );
        const previousOldChatsData = queryClient.getQueryData(getTeacherChats);
        queryClient.setQueryData(
          getTeacherChats,
          (oldData: ListTeacherChatResponseDto | undefined) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              data: oldData.data?.map((chat) => {
                return chat.id === Number(id)
                  ? { ...chat, title: variables.data.title }
                  : chat;
              }),
            };
          },
        );
        queryClient.setQueryData(
          getGetTeacherChatQueryKey(Number(id)),
          (oldData: GetChatResponseDto) => {
            return {
              ...oldData,
              title: variables.data.title,
            };
          },
        );

        return { previousSingleChatData, previousOldChatsData };
      },
      onError: (error, _variables, context) => {
        queryClient.setQueryData(
          getGetTeacherChatQueryKey(Number(id)),
          context?.previousSingleChatData,
        );
        queryClient.setQueryData(
          getTeacherChats,
          context?.previousOldChatsData,
        );
        console.error("Error updating chat title:", error);
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: getGetTeacherChatQueryKey(Number(id)),
        });
        queryClient.invalidateQueries({
          queryKey: getTeacherChats,
        });
      },
    },
  });
  const { data: teacherChat, isLoading } = useGetTeacherChat(Number(id), {
    query: {
      refetchInterval: (data) => {
        localStorage.setItem(
          "lastChatMessageCount",
          data?.state.data?.messages?.length.toString() || "0",
        );

        if (
          data?.state.data?.messages?.length &&
          data?.state.data?.messages[data?.state.data?.messages.length - 1]
            .role === "USER"
        ) {
          return 2000;
        }

        return false;
      },
      enabled: !!id,
    },
  });

  const selectedModel = teacherChat?.llmModel ?? selectedModelRaw;

  useEffect(() => {
    if (teacherChat?.id && teacherChat?.messages) {
      const allFiles: { name: string; s3Url: string }[] = [];
      teacherChat.messages.forEach((message) => {
        if (message.fileUri) {
          allFiles.push({
            name: message.fileName || message.fileUri.split("/").pop() || "",
            s3Url: message.fileUri,
          });
        }
      });
      setUploadedFiles(allFiles);
    }
  }, [teacherChat]);

  const { data: oldChatsRaw, queryKey: getTeacherChats } = useGetTeacherChats({
    size: 100,
  });

  const oldChats = useMemo(() => {
    // sort by createdAt
    const sortedChats = oldChatsRaw?.data?.sort((a, b) => {
      return (
        new Date(b.createdAt || "").getTime() -
        new Date(a.createdAt || "").getTime()
      );
    });
    return sortedChats;
  }, [oldChatsRaw]);

  const isWaitingForResponse = useMemo(() => {
    return teacherChat?.messages?.length &&
      teacherChat?.messages[teacherChat?.messages.length - 1].role === "USER"
      ? true
      : false;
  }, [teacherChat]);

  const { mutate: sendMessageToTeacherChat } = useSendMessageToTeacherChat({
    mutation: {
      onMutate: (variables) => {
        const previousData = queryClient.getQueryData(
          getGetTeacherChatQueryKey(Number(id)),
        );
        queryClient.setQueryData(
          getGetTeacherChatQueryKey(Number(id)),
          (oldData: GetChatResponseDto) => {
            return {
              ...oldData,
              messages: [
                ...(oldData.messages || []),
                {
                  id: Date.now(),
                  createdAt: new Date().toISOString(),
                  message: variables.data.message,
                  role: "USER",
                },
              ],
            };
          },
        );
        return { previousData };
      },
      onError: (error, _variables, context) => {
        queryClient.setQueryData(
          getGetTeacherChatQueryKey(Number(id)),
          context?.previousData,
        );
        console.error("Error sending message:", error);
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: getGetTeacherChatQueryKey(Number(id)),
        });
      },
    },
  });

  // İlk mesaj eklendiğinde header'ı otomatik olarak collapse et
  useEffect(() => {
    if (teacherChat?.messages?.length === 2) {
      setIsTopBarCollapsed(true);
    }
  }, [teacherChat?.messages?.length]);

  const handleSendMessage = () => {
    if (!message.trim()) return;

    const newMessage = message;
    setMessage("");

    // handleSendMessage içindeki setIsTopBarCollapsed(true) kaldırıldı

    if (newFile) {
      sendMessageToTeacherChat(
        {
          data: {
            contentType: newFile.s3Url?.includes(".pdf")
              ? TeacherChatSendMessageRequestDtoContentType.PDF
              : newFile.s3Url?.includes(".png")
                ? TeacherChatSendMessageRequestDtoContentType.IMAGE_PNG
                : TeacherChatSendMessageRequestDtoContentType.IMAGE_JPEG,
            message: newMessage,
            mediaUrl: newFile.s3Url,
            fileName: newFile.name,
            web: isWebSearchEnabled,
          },
          id: Number(id),
        },
        {},
      );
      setNewFile(null);
    } else {
      sendMessageToTeacherChat(
        {
          data: {
            contentType: TeacherChatSendMessageRequestDtoContentType.TEXT,
            message: newMessage,
            web: isWebSearchEnabled,
          },
          id: Number(id),
        },
        {},
      );
    }

    track(
      {
        category: "ContentSendMessage",
        action: "assistant.send.message",
        label: "Assistant send message",
        nonInteraction: false,
      },
      {
        location: "/assistant/send-message",
        element: "send-button",
        singleAsistantMessage: newMessage,
        modelId: selectedModel,
        web: isWebSearchEnabled,
      },
    );
  };

  const handleSendPredefinedMessage = (message: string) => {
    setMessage("");
    sendMessageToTeacherChat(
      {
        data: {
          contentType: TeacherChatSendMessageRequestDtoContentType.TEXT,
          message: message,
          web: isWebSearchEnabled,
        },
        id: Number(id),
      },
      {},
    );
  };

  const defaultAssistantColorScheme = assistantColorSchemes["EMPTY"];

  const visibleFollowups = useMemo(() => {
    return defaultAssistantColorScheme.followups[i18n.language as "en" | "tr"]
      .sort(() => Math.random() - 0.5)
      .slice(0, 4);
  }, [teacherChat]);

  const { trackEvent: track } = trackEvent();
  const handleClickNew = () => {
    track(
      {
        category: "ContentNew",
        action: "assistant.new",
        label: "Assistant new click",
        nonInteraction: false,
      },
      {
        location: "/assistant/new",
        element: "new-button",
      },
    );
    setShowHistory(false);
  };

  const handleClickHistory = () => {
    track(
      {
        category: "ContentHistory",
        action: "assistant.history",
        label: "Assistant history click",
        nonInteraction: false,
      },
      {
        location: "/assistant/history",
        element: "history-button",
      },
    );
    setShowHistory(true);
  };

  const handleClickPredifined = (followup: string) => {
    track(
      {
        category: "ContentPredefined",
        action: "assistant.predefined",
        label: "Assistant predefined click",
        nonInteraction: false,
      },
      {
        location: "/assistant/predefined",
        element: "predefined-button",
      },
    );
    return handleSendPredefinedMessage(followup);
  };

  useEffect(() => {
    if (!isWaitingForResponse && !isUploading) {
      textareaRef.current?.focus();
    }
  }, [isWaitingForResponse, isUploading]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Add useEffect to scroll when messages change
  useEffect(() => {
    scrollToBottom();
  }, [teacherChat?.messages]);

  const autoResizeTextarea = (element: HTMLTextAreaElement) => {
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px`;
  };

  // Initialize auto-resize and handle message changes
  useEffect(() => {
    if (textareaRef.current) {
      autoResizeTextarea(textareaRef.current);
    }
  }, [message]);

  const handleUpdateChatTitle = async () => {
    if (!editChatId || !editChatTitle.trim()) return;

    try {
      await updateTeacherChat({
        id: editChatId,
        data: {
          title: editChatTitle,
        },
      });
      setEditChatId(null);
      queryClient.invalidateQueries({
        queryKey: ["getTeacherChats"],
      });
      addToast({
        title: t("assistant.titleUpdated"),
        variant: "success",
        duration: 3000,
      });
    } catch (error) {
      console.error("Error updating chat title:", error);
      addToast({
        title: t("assistant.updateError"),
        variant: "error",
        duration: 3000,
      });
    }
  };

  const handleDeleteChat = async () => {
    if (!showDeleteModal) return;

    try {
      await deleteTeacherChat(showDeleteModal);
      setShowDeleteModal(undefined);
      //close history modal
      setShowHistory(false);
      //get the last chat id that is not the deleted chat
      const lastChatID = oldChats?.filter(
        (chat) => chat.id !== Number(showDeleteModal),
      )[0]?.id;
      queryClient.invalidateQueries({
        queryKey: getTeacherChats,
      });
      if (showDeleteModal === Number(id)) {
        // Redirect to latest chat if it exists, otherwise redirect to empty chat
        if (lastChatID) {
          navigate(`/assistants/new/empty/${lastChatID}`);
        } else {
          navigate("/assistants/new/empty");
        }
      }
      addToast({
        title: t("assistant.chatDeleted"),
        variant: "success",
        duration: 3000,
      });
    } catch (error) {
      console.error("Error deleting chat:", error);
      addToast({
        title: t("assistant.deleteError"),
        variant: "error",
        duration: 3000,
      });
    }
  };

  const handleModelChange = async (modelId: string) => {
    if (isModelChangeLoading) return;

    setIsModelChangeLoading(true);
    setLoadingModelId(modelId);

    try {
      await dispatch(setAssistantModel(modelId));
      await updateTeacherChat({
        id: Number(id),
        data: {
          llmModel: modelId,
        },
      });

      // You could also add analytics tracking here
      track(
        {
          category: "ModelChange",
          action: "assistant.model.change",
          label: "Assistant model change",
          nonInteraction: false,
        },
        {
          location: "/assistant/model-change",
          element: "model-selector",
          modelId: modelId,
        },
      );
    } catch (error) {
      console.error("Error changing model:", error);
      addToast({
        title: t("assistant.modelChangeError"),
        variant: "error",
        duration: 3000,
      });
    } finally {
      setIsModelChangeLoading(false);
      setLoadingModelId(null);
    }
  };

  return (
    <div
      className={`flex-1 lg:mt-5 bg-gray-50 dark:bg-dark-500 lg:rounded-tl-3xl overflow-y-auto h-full custom-scrollbar flex flex-col relative`}
    >
      {/* Header - Fixed height */}
      <div
        className={`transition-all duration-300 ease-in-out flex-none border-b dark:border-gray-800 bg-white dark:bg-dark-900 ${
          isTopBarCollapsed ? "h-0 overflow-hidden p-0 border-b-0" : "p-4"
        }`}
      >
        <div className="max-w-7xl mx-auto flex flex-col justify-between px-4">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center">
            <div className="flex items-center space-x-3">
              <Text variant={"h2"}>
                {teacherChat?.title || t("assistant.newChat")}
              </Text>
            </div>
            <div className="flex items-center space-x-2 mt-2 sm:mt-0">
              <Button
                onClick={handleClickHistory}
                textColor="text-gray-600 dark:text-gray-300"
                rounded="lg"
                size="md"
              >
                <History className="w-5 h-5" />
                <Text variant={"span"} size="base">
                  {t("assistant.viewHistory")}
                </Text>
              </Button>
              <Button
                onClick={() => setIsTopBarCollapsed(!isTopBarCollapsed)}
                textColor="text-gray-600 dark:text-gray-300"
                rounded="lg"
                size="md"
                title={
                  isTopBarCollapsed
                    ? t("assistant.expandHeader")
                    : t("assistant.collapseHeader")
                }
              >
                {isTopBarCollapsed ? (
                  <ChevronsDown className="w-5 h-5" />
                ) : (
                  <ChevronsUp className="w-5 h-5" />
                )}
              </Button>
              <Link
                to={`/assistants/new/empty`}
                onClick={handleClickNew}
                className="flex items-center space-x-2 px-4 py-2 rounded-full bg-indigo-50 hover:bg-indigo-100 dark:bg-dark-500 dark:hover:bg-dark-400 transition-colors"
              >
                <Plus className="w-5 h-5" />
                <span>{t("assistant.new")}</span>
              </Link>
            </div>
          </div>

          {/* Model selector on its own line */}
          <div className="mt-3 pt-3 sm:mt-4">
            <div className="">
              <ModelButtonSelector
                selectedModel={selectedModel}
                onModelChange={handleModelChange}
                isLoading={isModelChangeLoading}
                loadingModelId={loadingModelId}
                className="w-full h-full flex"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Expand button that appears when header is collapsed */}
      {isTopBarCollapsed && (
        <div className="absolute top-0 left-0 right-0 z-10 flex justify-center">
          <Button
            onClick={() => setIsTopBarCollapsed(false)}
            bgColor="bg-white/90 hover:bg-white dark:bg-dark-900 dark:hover:bg-dark-600"
            textColor="text-gray-600 dark:text-gray-300"
            rounded="lg"
            size="sm"
            className="px-6 border border-gray-200 dark:border-dark-400 shadow-md animate-bounce rounded-t-none"
          >
            <ChevronDown className="w-5 h-5" />
          </Button>
        </div>
      )}

      {isLoading ? (
        <SimpleLoading fastLoading={true} fullContainer={true} />
      ) : (
        <>
          {/* Chat Area - Flexible height */}
          <div className="flex-1 min-h-0 overflow-y-auto p-4 pb-12">
            <div className="max-w-7xl mx-auto space-y-6">
              {teacherChat?.messages?.map((message) => (
                <ChatBubble
                  key={message.id}
                  message={message}
                  avatar={avatar}
                  selectedModel={selectedModel}
                  isFirstMessage={message.id === teacherChat?.messages?.[0]?.id}
                />
              ))}
              {isWaitingForResponse && (
                <div className="flex justify-start mb-4 ml-2">
                  <div className="flex flex-row items-end space-x-4">
                    <div className="relative">
                      {/* Get the selected model's color scheme for the waiting indicator */}
                      {(() => {
                        const model =
                          availableModels.find((m) => m.id === selectedModel) ||
                          availableModels[2]; // Default to Claude

                        return (
                          <div className="relative w-5 h-5 transform hover:scale-110 transition-transform duration-300">
                            {/* Core glow */}
                            <div
                              className={`absolute inset-0 rounded-full ${model.color} shadow-lg`}
                            />

                            {/* Inner rapid pulse */}
                            <div
                              className={`absolute inset-0 rounded-full ${model.color} animate-[pulse_1s_ease-in-out_infinite]`}
                            />

                            {/* Outer slow pulse */}
                            <div
                              className={`absolute -inset-1 rounded-full ${model.color} animate-[pulse_2s_ease-in-out_infinite]`}
                            />

                            {/* Expanding rings */}
                            <div
                              className={`absolute -inset-2 rounded-full ${model.color} animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite]`}
                            />
                            <div
                              className={`absolute -inset-3 rounded-full ${model.color} animate-[ping_2.5s_cubic-bezier(0,0,0.2,1)_infinite]`}
                            />

                            {/* Rotating gradient overlay */}
                            <div
                              className={`absolute inset-0 rounded-full ${model.activeBgColor} animate-[spin_4s_linear_infinite]`}
                            />

                            {/* Sparkle effects */}
                            <div className="absolute -inset-4 flex items-center justify-center">
                              <div
                                className="w-1.5 h-1.5 bg-white rounded-full animate-[ping_1.5s_ease-out_infinite] delay-100"
                                style={{ transform: "translate(10px, 10px)" }}
                              />
                              <div
                                className="w-1.5 h-1.5 bg-white rounded-full animate-[ping_1.5s_ease-out_infinite] delay-300"
                                style={{ transform: "translate(-10px, -10px)" }}
                              />
                              <div
                                className="w-1.5 h-1.5 bg-white rounded-full animate-[ping_1.5s_ease-out_infinite] delay-500"
                                style={{ transform: "translate(-10px, 10px)" }}
                              />
                              <div
                                className="w-1.5 h-1.5 bg-white rounded-full animate-[ping_1.5s_ease-out_infinite] delay-700"
                                style={{ transform: "translate(10px, -10px)" }}
                              />
                            </div>

                            {/* Center dot */}
                            <div className="absolute inset-[25%] rounded-full bg-white shadow-inner animate-[pulse_2s_ease-in-out_infinite]" />
                          </div>
                        );
                      })()}
                    </div>
                    <div
                      className={`relative px-5 pt-3 pb-8 rounded-2xl rounded-bl-none min-h-[60px] ${(() => {
                        const model =
                          availableModels.find((m) => m.id === selectedModel) ||
                          availableModels[2];
                        const colorBase = model.color.replace("text-", "");
                        const colorClass = colorBase.split(" ")[0]; // Extract color base
                        return `bg-gradient-to-br from-${colorClass}-50 to-${colorClass}-50 dark:from-${colorClass}-900/30 dark:to-${colorClass}-900/30 border border-${colorClass}-200/50 dark:border-${colorClass}-700/50`;
                      })()}`}
                    >
                      {/* Main thinking area */}
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="text-gray-600 dark:text-gray-300 animate-pulse">
                          {t("toolChat.thinking")}
                        </span>
                      </div>

                      {/* Model name in bottom right */}
                      {selectedModel && (
                        <div className="absolute bottom-2 right-3">
                          <div className="flex items-center gap-1">
                            {(() => {
                              const model = availableModels.find(
                                (m) => m.id === selectedModel,
                              );
                              const Icon =
                                model?.icon || availableModels[0].icon;
                              return (
                                <>
                                  {isModelChangeLoading ? (
                                    <Loader2 className="w-3 h-3 animate-spin text-gray-400 dark:text-gray-500" />
                                  ) : (
                                    <Icon className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                                  )}
                                  <span className="text-xs text-gray-400 dark:text-gray-500 truncate max-w-[80px]">
                                    {isModelChangeLoading && loadingModelId
                                      ? availableModels.find(
                                          (m) => m.id === loadingModelId,
                                        )?.name || t("assistant.ai")
                                      : model?.name || t("assistant.ai")}
                                  </span>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input Area - Fixed height */}
          <div className="max-w-7xl mx-auto mb-4">
            {teacherChat?.messages?.length === 0 ||
              (teacherChat?.messages?.length === 1 && (
                <div className="grid md:grid-cols-2 grid-cols-1 gap-4 mb-4 mx-4 sm:mx-1">
                  {visibleFollowups.map((followup, index) => (
                    <Button
                      key={index}
                      onClick={() => {
                        handleClickPredifined(followup);
                      }}
                      rounded="full"
                      variant="outline"
                      bgColor="hover:bg-gray-50 dark:hover:bg-dark-900"
                      textColor="text-gray-700 dark:text-gray-300"
                      size="sm"
                      className="border-gray-200 dark:border-dark-400 hover:border-primary-500 dark:hover:border-primary-400"
                    >
                      {followup}
                    </Button>
                  ))}
                </div>
              ))}
          </div>
          <div className="flex-none pb-4 px-4 pt-2">
            <div className="max-w-7xl mx-auto">
              <div className="text-center text-xs text-gray-600 dark:text-gray-400">
                {t("assistant.aiDisclaimer")}
              </div>
              {uploadedFiles && uploadedFiles.length > 0 && (
                <div className="max-w-7xl mx-auto mb-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {uploadedFiles.map((file, i) => (
                      <FilePreview
                        key={i}
                        fileName={file.name || ""}
                        fileUrl={file.s3Url || ""}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center space-x-4">
                <div className="flex-1 relative flex items-center my-2">
                  <textarea
                    ref={textareaRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    data-testid="message-input"
                    placeholder={
                      isWaitingForResponse
                        ? t("assistant.waitingForResponse")
                        : t("assistant.typePlaceholder")
                    }
                    className={`w-full px-4 py-3 pr-24 pl-5 rounded-3xl border border-dark-600 dark:border-dark-600 bg-white dark:bg-dark-900 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none ${(() => {
                      const model =
                        availableModels.find((m) => m.id === selectedModel) ||
                        availableModels[2];
                    })()} focus:border-transparent transition-opacity duration-200 min-h-14 max-h-96 resize-none overflow-y-auto ${
                      isWaitingForResponse || isUploading
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                    disabled={isWaitingForResponse || isUploading}
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        !e.shiftKey &&
                        !isWaitingForResponse &&
                        !isUploading
                      ) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />
                  <div className="absolute right-3 top-[50%] -translate-y-[50%] flex items-center space-x-2">
                    <div className="relative">
                      <button
                        onClick={() =>
                          setIsWebSearchEnabled(!isWebSearchEnabled)
                        }
                        className={`flex items-center w-[68px] h-[30px] rounded-full relative transition-colors duration-300 ${
                          isWebSearchEnabled
                            ? `bg-blue-500 dark:bg-blue-600`
                            : "bg-gray-200 dark:bg-dark-400"
                        }`}
                        title={
                          isWebSearchEnabled
                            ? t("assistant.disableWebSearch")
                            : t("assistant.enableWebSearch")
                        }
                        aria-label={
                          isWebSearchEnabled
                            ? t("assistant.disableWebSearch")
                            : t("assistant.enableWebSearch")
                        }
                        disabled={isWaitingForResponse || isUploading}
                      >
                        <div
                          className={`absolute w-[26px] h-[26px] rounded-full shadow-md transform transition-transform duration-300 flex items-center justify-center
                            ${
                              isWebSearchEnabled
                                ? "translate-x-[40px] bg-white dark:bg-dark-900"
                                : "translate-x-[2px] bg-white dark:bg-dark-600"
                            }
                          `}
                        >
                          {isWebSearchEnabled ? (
                            <Globe
                              className={`w-4 h-4 ${(() => {
                                const model =
                                  availableModels.find(
                                    (m) => m.id === selectedModel,
                                  ) || availableModels[2];
                                const colorBase = model.color.replace(
                                  "text-",
                                  "",
                                );
                                const colorClass = colorBase.split(" ")[0];
                                return `text-${colorClass}-500`;
                              })()} animate-pulse`}
                            />
                          ) : (
                            <div className="relative">
                              <Globe className="w-4 h-4 text-gray-400" />
                              <div className="absolute w-[1.5px] h-4 bg-gray-400 top-0 left-1/2 -translate-x-1/2 rotate-45 rounded-full" />
                            </div>
                          )}
                        </div>
                        <span className="sr-only sm:not-sr-only sm:ml-3 text-[10px] font-medium text-white">
                          {isWebSearchEnabled ? t("assistant.web") : ""}
                        </span>
                      </button>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block">
                        <div className="bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                          {isWebSearchEnabled
                            ? t("assistant.disableWebSearch")
                            : t("assistant.enableWebSearch")}
                        </div>
                      </div>
                    </div>
                    <FileUploader
                      small
                      maxSize={50 * 1024 * 1024}
                      shouldExtractContent={false}
                      accept={{
                        "application/pdf": [".pdf"],
                        "image/png": [".png"],
                        "image/jpeg": [".jpg", ".jpeg"],
                      }}
                      shouldOnlyGeneratePresignedUrl
                      onUploadSuccess={async (fileUrl, response) => {
                        setNewFile({
                          name:
                            response?.fileName ||
                            fileUrl.split("/").pop() ||
                            "",
                          s3Url: fileUrl,
                        });
                        const allFiles = [...uploadedFiles];
                        allFiles.push({
                          name: response?.fileName || "",
                          s3Url: fileUrl,
                        });
                        setUploadedFiles(allFiles);
                        setIsUploading(false);
                        addToast({
                          title: t("assistant.fileUploaded"),
                          description: t("assistant.fileUploadedDescription"),
                          variant: "success",
                          duration: 3000,
                        });
                      }}
                      onUploadError={(error) => {
                        setIsUploading(false);
                        addToast({
                          title: t("assistant.uploadError"),
                          description: error,
                          variant: "error",
                          duration: 3000,
                        });
                      }}
                      onUploadProgress={(progress) => {
                        if (progress > 0 && progress !== 100) {
                          setIsUploading(true);
                          // setUploadProgress(progress);
                        }
                      }}
                      uploadColor="purple"
                      extractColor="purple"
                      successColor="green"
                      className="w-10 h-10 p-0 flex items-center justify-center"
                      style={{ minHeight: "unset" }}
                    />
                  </div>
                </div>
                <Button
                  onClick={handleSendMessage}
                  disabled={
                    !message.trim() || isWaitingForResponse || isUploading
                  }
                  bgColor={(() => {
                    const model =
                      availableModels.find((m) => m.id === selectedModel) ||
                      availableModels[2];
                    const colorBase = model.color.replace("text-", "");
                    const colorClass = colorBase.split(" ")[0];
                    return `bg-${colorClass}-600 hover:bg-${colorClass}-700 dark:bg-${colorClass}-700 dark:hover:bg-${colorClass}-800`;
                  })()}
                  rounded="lg"
                  className={`w-10 h-10 transition-all duration-200 ${
                    !message.trim() || isWaitingForResponse || isUploading
                      ? "opacity-40 cursor-not-allowed filter grayscale"
                      : "transform hover:-translate-y-0.5 active:translate-y-0"
                  }`}
                  data-testid="send-message-button"
                >
                  <SendHorizontal
                    className={`w-5 h-5 ${isWaitingForResponse ? "animate-pulse" : ""}`}
                  />
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      <Modal
        show={showHistory}
        onClose={() => {
          setShowHistory(false);
          setEditChatId(null);
        }}
        title={t("assistant.chatHistory")}
      >
        <div className="space-y-4 max-h-[360px] overflow-y-auto">
          {oldChats?.map((chat) => (
            <div
              key={chat.id}
              className={`flex flex-col p-4 mx-4 rounded-lg border transition-all overflow-hidden ${
                chat.id === Number(id)
                  ? "border-violet-500 dark:border-violet-400 bg-violet-50 dark:bg-violet-900/20 shadow-lg shadow-violet-500/10"
                  : "border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
              } group relative`}
            >
              {chat.id === Number(id) && (
                <div className="absolute left-0 top-0 bottom-0 w-1 rounded-full bg-violet-500 dark:bg-violet-400" />
              )}

              {editChatId === chat.id ? (
                // Edit mode for this chat item
                <div className="flex flex-col w-full space-y-2">
                  <input
                    type="text"
                    value={editChatTitle}
                    onChange={(e) => setEditChatTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-violet-300 dark:border-violet-600 rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-800 dark:text-white"
                    placeholder={t("assistant.enterChatTitle")}
                    autoFocus
                  />
                  <div className="flex justify-end space-x-2">
                    <Button
                      variant="solid"
                      rounded="lg"
                      size="sm"
                      bgColor="bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
                      textColor="text-gray-800 dark:text-gray-100"
                      onClick={() => setEditChatId(null)}
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button
                      variant="solid"
                      size="sm"
                      bgColor="bg-primary-600 dark:bg-primary-500 hover:bg-primary-700 dark:hover:bg-primary-600"
                      rounded="lg"
                      textColor="text-white"
                      // disabled={isSaving}
                      onClick={handleUpdateChatTitle}
                    >
                      {t("common.save")}
                    </Button>
                  </div>
                </div>
              ) : (
                // Normal display mode
                <Link
                  to={`/assistants/new/empty/${chat.id}`}
                  onClick={() => setShowHistory(false)}
                  className="flex sm:flex-row flex-col items-center transform hover:scale-[1.02]"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <h4
                        className={`text-sm font-medium truncate ${
                          chat.id === Number(id)
                            ? "text-violet-900 dark:text-violet-100"
                            : "text-gray-900 dark:text-white"
                        }`}
                      >
                        {chat.title ||
                          chat.description ||
                          t("assistant.noDescription")}
                      </h4>
                    </div>
                    <p
                      className={`mt-1 text-sm ${
                        chat.id === Number(id)
                          ? "text-violet-600 dark:text-violet-300"
                          : "text-gray-500 dark:text-gray-400"
                      } truncate`}
                    >
                      {format(
                        new Date(chat.createdAt || ""),
                        "d MMMM yyyy HH:mm",
                        {
                          locale: i18n.language === "tr" ? tr : enUS,
                        },
                      )}
                    </p>
                  </div>
                  <div className="flex flex-row items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      rounded="full"
                      className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (chat.id) {
                          setEditChatId(chat.id);
                          setEditChatTitle(
                            chat.title || chat.description || "",
                          );
                        }
                      }}
                    >
                      <Pencil className="w-4 h-4 text-gray-500 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      rounded="full"
                      className="p-1.5 hover:bg-red-100/30 dark:hover:bg-red-900/20"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (chat.id) {
                          setShowDeleteModal(chat.id);
                        }
                      }}
                    >
                      <Trash2 className="w-4 h-4 text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400" />
                    </Button>
                    <ChevronRight
                      className={`w-5 h-5 ml-4 ${
                        chat.id === Number(id)
                          ? "text-violet-500 dark:text-violet-400"
                          : "text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300"
                      }`}
                    />
                  </div>
                </Link>
              )}
            </div>
          ))}
          {(!oldChats || oldChats.length === 0) && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              {t("assistant.noChatsYet")}
            </div>
          )}
        </div>
      </Modal>
      {showDeleteModal !== undefined && (
        <DeleteConfirmPopUp
          handleDelete={handleDeleteChat}
          showDeletePopup={showDeleteModal}
          setShowDeletePopup={setShowDeleteModal}
          title={t("assistant.confirmDelete")}
        />
      )}
    </div>
  );
};

export default SingleAssistantWrapper;
